<script>
    import { formatTonnes, formatDuration, formatDateTime } from './reportHelpers.js';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    export let summaries = [];
    export let kpis = {};
    export let searchTerm = '';
    export let reportMode = 'job'; // 'job' | 'date'
    export let reportTitle = '';

    // Filter summaries by search
    $: filteredSummaries = summaries.filter(s => {
        if (!searchTerm || !searchTerm.trim()) return true;
        const q = searchTerm.toLowerCase().trim();
        return (
            s.rego.toLowerCase().includes(q) ||
            s.driver.toLowerCase().includes(q) ||
            s.jobNumber.toLowerCase().includes(q) ||
            s.customerName.toLowerCase().includes(q) ||
            s.materialName.toLowerCase().includes(q)
        );
    });

    function viewTruckLoads(truckRego) {
        dispatch('selectTruck', { truckId: truckRego });
    }
</script>

<div class="summary-view-container">
    <!-- Executive KPI Row -->
    <div class="kpi-grid">
        <div class="kpi-card">
            <div class="kpi-label">Total Loads</div>
            <div class="kpi-value text-primary">{(kpis.totalLoads || 0).toLocaleString()}</div>
            <div class="kpi-subtext">Completed tipper loads</div>
        </div>

        <div class="kpi-card">
            <div class="kpi-label">Total Tonnes Hauled</div>
            <div class="kpi-value text-success">{formatTonnes(kpis.totalTonnes || 0, 0)}</div>
            <div class="kpi-subtext">Across {kpis.activeTrucksCount || 0} active vehicles</div>
        </div>

        <div class="kpi-card">
            <div class="kpi-label">Active Fleet</div>
            <div class="kpi-value font-mono">{kpis.activeTrucksCount || 0} Trucks</div>
            <div class="kpi-subtext">{kpis.activeDriversCount || 0} drivers deployed</div>
        </div>

        <div class="kpi-card">
            <div class="kpi-label">Avg Turnaround</div>
            <div class="kpi-value font-mono">{formatDuration(kpis.avgCycleMins || 0)}</div>
            <div class="kpi-subtext">{formatDuration(kpis.avgTripMins || 0)} transit leg</div>
        </div>

        <div class="kpi-card">
            <div class="kpi-label">Operational Span</div>
            <div class="kpi-value font-mono" style="font-size: 1.15rem;">
                {kpis.firstLoadTime ? formatDateTime(kpis.firstLoadTime, true) : '--:--'} – {kpis.lastLoadTime ? formatDateTime(kpis.lastLoadTime, true) : '--:--'}
            </div>
            <div class="kpi-subtext">{kpis.spanFormatted || 'Active'}</div>
        </div>
    </div>

    <!-- Main Truck Breakdown Card -->
    <div class="table-card">
        <div class="table-card-header">
            <div>
                <h4 class="table-title">Fleet Vehicles Haulage Summary</h4>
                <p class="table-subtitle">Summary of completed loads and tonnage per truck for {reportTitle} ({reportMode === 'date' ? 'Daily Shift' : 'Job Lifecycle'})</p>
            </div>
            <div class="record-counter">
                {filteredSummaries.length} {filteredSummaries.length === 1 ? 'Truck' : 'Trucks'}
            </div>
        </div>

        {#if filteredSummaries.length === 0}
            <div class="table-empty-state">
                <p>No loads recorded for the selected parameters.</p>
            </div>
        {:else}
            <div class="table-responsive">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th>Truck Rego</th>
                            <th>Driver</th>
                            <th>Payload Cap</th>
                            <th>Job # & Client</th>
                            <th>Material</th>
                            <th>First Load</th>
                            <th>Last Tip</th>
                            <th class="text-right">Completed Loads</th>
                            <th class="text-right">Total Hauled</th>
                            <th class="text-right">Avg Turnaround</th>
                            <th class="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each filteredSummaries as s}
                            <tr class="data-row">
                                <td>
                                    <span class="rego-chip">{s.rego}</span>
                                </td>
                                <td class="font-medium">{s.driver}</td>
                                <td class="font-mono text-muted">{s.capacity}t</td>
                                <td>
                                    <div class="job-stack">
                                        <span class="job-badge">{s.jobNumber}</span>
                                        <span class="client-name">{s.customerName}</span>
                                    </div>
                                </td>
                                <td class="text-muted">{s.materialName}</td>
                                <td class="font-mono text-muted">{formatDateTime(s.firstLoadTime, true)}</td>
                                <td class="font-mono text-muted">{formatDateTime(s.lastLoadTime, true)}</td>
                                <td class="text-right font-mono font-bold text-primary">{s.loadsCount}</td>
                                <td class="text-right font-mono font-bold">{formatTonnes(s.totalTonnes, 0)}</td>
                                <td class="text-right font-mono">{formatDuration(s.avgCycleMins)}</td>
                                <td class="text-center">
                                    <button
                                        type="button"
                                        class="btn-view-loads"
                                        on:click={() => viewTruckLoads(s.rego)}
                                        title="View load-by-load docket details for {s.rego}"
                                    >
                                        View Loads →
                                    </button>
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
    </div>
</div>

<style>
    .summary-view-container {
        display: flex;
        flex-direction: column;
        gap: 1.25rem;
    }

    .kpi-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 1rem;
    }

    .kpi-card {
        background-color: #ffffff;
        border: 1px solid var(--tt-slate-200);
        border-radius: var(--tt-radius-md);
        padding: 1rem 1.25rem;
        box-shadow: 0 1px 2px rgba(0,0,0,0.03);
        display: flex;
        flex-direction: column;
    }

    .kpi-label {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--tt-slate-500);
        margin-bottom: 0.25rem;
    }

    .kpi-value {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--tt-slate-900);
        line-height: 1.2;
    }

    .kpi-subtext {
        font-size: 0.75rem;
        color: var(--tt-slate-500);
        margin-top: 0.35rem;
    }

    .table-card {
        background-color: #ffffff;
        border: 1px solid var(--tt-slate-200);
        border-radius: var(--tt-radius-md);
        box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        overflow: hidden;
    }

    .table-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 1.25rem;
        background-color: var(--tt-slate-50);
        border-bottom: 1px solid var(--tt-slate-200);
    }

    .table-title {
        margin: 0;
        font-size: 0.9375rem;
        font-weight: 700;
        color: var(--tt-slate-900);
    }

    .table-subtitle {
        margin: 2px 0 0 0;
        font-size: 0.75rem;
        color: var(--tt-slate-500);
    }

    .record-counter {
        font-size: 0.75rem;
        font-weight: 600;
        background-color: var(--tt-slate-200);
        color: var(--tt-slate-700);
        padding: 3px 8px;
        border-radius: 999px;
    }

    .table-responsive {
        width: 100%;
        overflow-x: auto;
    }

    .data-table {
        width: 100%;
        border-collapse: collapse;
        text-align: left;
        font-size: 0.8125rem;
    }

    .data-table th {
        background-color: #ffffff;
        color: var(--tt-slate-600);
        font-size: 0.6875rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 10px 12px;
        border-bottom: 1px solid var(--tt-slate-200);
        white-space: nowrap;
    }

    .data-table td {
        padding: 10px 12px;
        border-bottom: 1px solid var(--tt-slate-100);
        color: var(--tt-slate-800);
        vertical-align: middle;
    }

    .data-row:hover {
        background-color: var(--tt-slate-50);
    }

    .rego-chip {
        display: inline-block;
        background-color: #fef08a;
        color: #000000;
        font-family: monospace;
        font-weight: 700;
        padding: 2px 6px;
        border-radius: 3px;
        border: 1px solid #eab308;
    }

    .job-stack {
        display: flex;
        flex-direction: column;
        gap: 1px;
    }

    .job-badge {
        font-family: monospace;
        font-weight: 600;
        color: var(--tt-slate-700);
    }

    .client-name {
        font-size: 0.75rem;
        color: var(--tt-slate-500);
    }

    .btn-view-loads {
        background-color: var(--tt-primary-light);
        border: 1px solid var(--tt-primary-border);
        color: var(--tt-primary);
        font-size: 0.75rem;
        font-weight: 600;
        padding: 4px 10px;
        border-radius: var(--tt-radius-sm);
        cursor: pointer;
        transition: all 0.15s ease;
        white-space: nowrap;
    }

    .btn-view-loads:hover {
        background-color: #dbeafe;
        color: var(--tt-primary-hover);
        border-color: var(--tt-primary);
    }

    .text-right {
        text-align: right;
    }

    .text-center {
        text-align: center;
    }

    .text-muted {
        color: var(--tt-slate-500);
    }

    .text-primary {
        color: var(--tt-primary);
    }

    .text-success {
        color: #10b981;
    }

    .font-mono {
        font-family: monospace;
    }

    .font-medium {
        font-weight: 600;
    }

    .font-bold {
        font-weight: 700;
    }

    .table-empty-state {
        padding: 3rem 1.5rem;
        text-align: center;
        color: var(--tt-slate-500);
        font-size: 0.875rem;
    }
</style>
